import React, { useMemo } from "react";
import "../../../../designsystem/KC_Card/styles.scss";
import KC_Heading4 from "../../../../designsystem/typography/KC-Heading/KC_Heading4";
import KC_UIText from "../../../../designsystem/typography/KC_UIText/KC_UIText";
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";
import { type Distribution } from "../../model/AnalyticsData";
import { GRAPH_COLORS } from "../../../../designsystem/KC_Card/constants";
import Legend from "./Legend";
import GraphWithLegendLayout from "./GraphWithLegendLayout";
import { tt } from "../../../../utils/i18n/i18n";
import GRAPH_MIN_HEIGHT from "./constants";
import TSUtils from "../../../../utils/helpers/TSUtils";
import CurrencyProp from "../model/CurrencyProp";
import KC_Heading2 from "../../../../designsystem/typography/KC-Heading/KC_Heading2";
import {
  DEFAULT_CURRENCY,
  DEFAULT_CURRENCY_SUB_UNITS
} from "@tootsweet/model/enums/Currencies";

interface Props {
  title: string;
  text?: string | number;
  distribution?: Distribution[];
  getDisplayNameForLabel?: (_id: any) => string;
  suffix?: string;
  isAmount?: boolean;
  groupSourcesByLabel?: boolean;
  currency?: CurrencyProp;
}

interface PieData {
  name: string;
  value: number;
  color?: string;
}

const INNER_RADIUS = 80;

export default function PieGraph(props: Props) {
  const pieData = useMemo<PieData[]>(() => {
    let res: PieData[] = [];

    if (props.distribution?.length) {
      res = props.distribution.map((d) => {
        return {
          name: props.getDisplayNameForLabel?.(d._id) || d._id,
          value: props.isAmount
            ? d.count / (props.currency?.subUnits || DEFAULT_CURRENCY_SUB_UNITS)
            : d.count
        };
      });

      if (props.groupSourcesByLabel) {
        const grouped = res
          .reduce((acc, item) => {
            const existing = acc.get(item.name);
            if (existing) {
              existing.value += item.value;
            } else {
              acc.set(item.name, { ...item });
            }
            return acc;
          }, new Map())
          .values();
        res = Array.from(grouped);
      }
    }

    res.forEach((item, index) => {
      item.color = GRAPH_COLORS[index % GRAPH_COLORS.length];
    });

    return res;
  }, [props.distribution, props.groupSourcesByLabel]);

  const total = useMemo(() => {
    let res = 0;
    props.distribution?.forEach((d) => (res += d.count));
    return res;
  }, [props.distribution]);

  const pieTotal = useMemo(() => {
    if (props.isAmount) {
      return TSUtils.formatAmountUsingIntl(
        total,
        props.currency?.subUnits || DEFAULT_CURRENCY_SUB_UNITS,
        props.currency?.trigram || DEFAULT_CURRENCY
      );
    }
    return `${total} ${props.suffix || ""}`;
  }, [total, props.suffix, props.currency, props.isAmount]);

  const renderGraph = () => {
    if (!total) {
      return <>{tt("analytics_nothing_to_display")}</>;
    }

    return (
      <>
        <ResponsiveContainer
          width="100%"
          height="100%"
          minHeight={GRAPH_MIN_HEIGHT}
          debounce={500}
        >
          <PieChart
            margin={{ top: 16, right: 16, bottom: 16, left: 16 }}
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              height: "100%"
            }}
          >
            <Pie
              data={pieData}
              cx="50%"
              cy="50%"
              dataKey="value"
              nameKey="name"
              paddingAngle={5}
              innerRadius={INNER_RADIUS}
              outerRadius={130}
              cornerRadius={4}
            >
              {pieData.map((datum, index) => (
                <Cell key={`cell-${index}`} fill={datum.color} />
              ))}
            </Pie>
            <Tooltip
              wrapperStyle={{
                zIndex: 20
              }}
            />
          </PieChart>
        </ResponsiveContainer>
        <div
          className="d-flex align-items-center justify-content-center position-absolute"
          style={{
            width: `${INNER_RADIUS * 2}px`,
            height: `${INNER_RADIUS * 2}px`,
            left: "50%",
            top: "50%",
            transform: "translate(-50%, -50%)"
          }}
        >
          <KC_Heading2 text={pieTotal} className="text-center w-100" />
        </div>
      </>
    );
  };

  return (
    <div className="d-flex flex-column kc-card-dashboard kc-card-dashboard-with-graph">
      <div className="d-flex flex-column st-gap">
        <KC_Heading4 text={props.title} />
        {!!props.text && <KC_UIText weight="reg" text={props.text} />}
      </div>
      <GraphWithLegendLayout
        graph={renderGraph()}
        legend={total ? <Legend data={pieData} /> : <></>}
      />
    </div>
  );
}
